import React, { useState, useEffect } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { ListSkeleton } from '../components/ui';

const STATO_COLOR = {
  attivo:   { bg:'var(--green-dim)', color:'var(--green)', dot:'var(--green)', label:'Attivo' },
  inattivo: { bg:'var(--surface)', color:'var(--text-muted)', dot:'var(--text-dim)', label:'Inattivo' },
  in_pausa: { bg:'var(--yellow-dim)', color:'var(--yellow)', dot:'var(--yellow)', label:'In pausa' },
};

function fmtTime(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleTimeString('it-IT', { hour:'2-digit', minute:'2-digit' });
}

export default function AgentiVivi() {
  const [agenti, setAgenti] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  // Il feed log globale è dato sensibile: solo admin/boss. Gli operatori vedono
  // lo stato degli agenti senza il feed dei log di sistema.
  const isAdmin = user?.ruolo === 'admin' || user?.ruolo === 'boss';

  const fetchData = () => {
    // Le due chiamate sono indipendenti: le card agenti si caricano anche se
    // l'utente non ha accesso ai log (evita che un 403 svuoti tutta la pagina).
    api.get('/eden_hq/agenti')
      .then(a => setAgenti(a.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
    if (isAdmin) {
      api.get('/logs?limit=30', { silent: true })
        .then(l => setLogs(l.data?.logs || l.data || []))
        .catch(() => {});
    }
  };

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // Per ogni agente, trova l'ultimo log correlato
  const agenteLogs = (nomeAgente) => {
    return logs.filter(l => l.utente_email?.includes(nomeAgente.toLowerCase()) || l.azione?.toLowerCase().includes(nomeAgente.toLowerCase()));
  };

  return (
    <div className="eden-page">
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:700, margin:'0 0 4px', color:'var(--text)' }}>Agenti dal vivo</h1>
          <p style={{ fontSize:13, color:'var(--text-muted)', margin:0 }}>Stato in tempo reale degli agenti. Aggiornato ogni 15 secondi.</p>
        </div>
        <div style={{ fontSize:11, color:'var(--text-dim)', display:'flex', alignItems:'center', gap:6 }}>
          <div style={{ width:7, height:7, borderRadius:'50%', background:'var(--green)', animation:'pulse 2s infinite' }}/>
          Live
        </div>
      </div>

      {loading ? (
        <ListSkeleton rows={5} />
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px,1fr))', gap:12 }}>
          {agenti.map(a => {
            const sc = STATO_COLOR[a.stato] || STATO_COLOR.inattivo;
            const recentLog = logs.find(l => l.azione?.toLowerCase().includes(a.nome.toLowerCase().split(' ')[0]));
            return (
              <div key={a.id} style={{ background:'var(--surface)', border:`1px solid ${a.stato === 'attivo' ? 'rgba(16,185,129,0.25)' : 'var(--border)'}`, borderRadius:14, padding:'16px 18px' }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <div style={{ width:38, height:38, borderRadius:'50%', background:'var(--purple-dim)', border:'1px solid rgba(168,85,247,0.3)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18 }}>🤖</div>
                    <div>
                      <div style={{ fontSize:14, fontWeight:700, color:'var(--text)' }}>{a.nome}</div>
                      <div style={{ fontSize:11.5, color:'var(--text-muted)' }}>{a.ruolo}</div>
                    </div>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:6, background:sc.bg, padding:'4px 10px', borderRadius:8 }}>
                    <div style={{ width:7, height:7, borderRadius:'50%', background:sc.dot }}/>
                    <span style={{ fontSize:11.5, color:sc.color, fontWeight:500 }}>{sc.label}</span>
                  </div>
                </div>

                {a.descrizione && (
                  <div style={{ fontSize:12, color:'var(--text-muted)', marginBottom:10, lineHeight:1.5 }}>
                    {a.descrizione}
                  </div>
                )}

                {a.playbook_collegati?.length > 0 && (
                  <div style={{ marginBottom:10 }}>
                    <div style={{ fontSize:10.5, color:'var(--text-dim)', marginBottom:4, textTransform:'uppercase', letterSpacing:'0.04em' }}>Playbook collegati</div>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                      {a.playbook_collegati.map(pb => (
                        <span key={pb} style={{ fontSize:10.5, background:'var(--purple-dim)', color:'var(--purple)', padding:'2px 7px', borderRadius:6 }}>{pb}</span>
                      ))}
                    </div>
                  </div>
                )}

                {isAdmin && (
                <div style={{ borderTop:'1px solid var(--border)', paddingTop:10, marginTop:6 }}>
                  <div style={{ fontSize:10.5, color:'var(--text-dim)', marginBottom:4 }}>Ultima attività (dai log)</div>
                  {recentLog ? (
                    <div style={{ fontSize:11.5, color:'var(--text)' }}>
                      <code style={{ fontSize:10, color:'var(--cyan)', background:'var(--cyan-dim)', padding:'1px 5px', borderRadius:4, marginRight:5 }}>{recentLog.metodo}</code>
                      {recentLog.path}
                      <span style={{ color:'var(--text-dim)', marginLeft:6, fontSize:10.5 }}>{fmtTime(recentLog.creato_il)}</span>
                    </div>
                  ) : (
                    <div style={{ fontSize:11.5, color:'var(--text-dim)' }}>Nessun log recente</div>
                  )}
                </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Log feed — solo admin/boss (contiene log di sistema) */}
      {isAdmin && (
      <div style={{ marginTop:32 }}>
        <div style={{ fontSize:12, fontWeight:600, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:12 }}>
          Feed log recente
        </div>
        <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, overflow:'hidden' }}>
          <div style={{ overflowX:'auto', WebkitOverflowScrolling:'touch' }}>
          {logs.slice(0,15).map((l, i) => (
            <div key={l.id || i} style={{ display:'grid', gridTemplateColumns:'1fr 1.5fr 1fr 0.7fr 0.6fr', alignItems:'center', padding:'9px 14px', borderBottom: i < 14 ? '1px solid var(--border)' : 'none', fontSize:12, minWidth:480 }}>
              <span style={{ color:'var(--text)', fontWeight:500 }}>{l.utente_email?.split('@')[0] || 'sistema'}</span>
              <code style={{ fontSize:10.5, color:'var(--text-muted)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{l.metodo} {l.path}</code>
              <span style={{ fontSize:11, color:'var(--text-muted)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{l.azione || '—'}</span>
              <span style={{ fontSize:10.5, color:'var(--text-dim)' }}>{fmtTime(l.creato_il)}</span>
              <span style={{ fontSize:10, fontWeight:600, color: l.esito === 'ok' ? 'var(--green)' : l.esito === 'warn' ? 'var(--yellow)' : 'var(--red)', background: l.esito === 'ok' ? 'var(--green-dim)' : l.esito === 'warn' ? 'var(--yellow-dim)' : 'var(--red-dim)', padding:'2px 7px', borderRadius:5, textAlign:'center' }}>{l.esito || 'ok'}</span>
            </div>
          ))}
          </div>{/* end scroll */}
        </div>
      </div>
      )}
    </div>
  );
}
